import React from "react";
import dayjs from "dayjs";
import { Button } from "../ui";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { processService } from "@/services/process";
import { Skeleton } from "@/components/ui/skeleton";

interface ViewProcessFormsModalProps {
  isOpen: boolean;
  onClose: () => void;
  processId: string;
}

const ViewProcessFormsModal: React.FC<ViewProcessFormsModalProps> = ({
  isOpen,
  onClose,
  processId,
}) => {
  const { t } = useTranslation();
  
  // Only fetch when modal is open
  const { data: processData, isLoading } = useQuery({
    queryKey: ['process', processId],
    queryFn: () => processService.getProcessById({ queryKey: ['process', processId] }),
    enabled: isOpen && !!processId,
  });

  // Extract forms from the process data
  const forms = processData?.data?.forms || [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:rounded-md max-w-[640px]">
        <DialogHeader>
          <DialogTitle className="text-center text-lg sm:text-xl font-semibold">
            {t("processManagement.processForms")}
          </DialogTitle>
          <DialogDescription>
            {t("processManagement.viewProcessFormsDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="space-y-3 p-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-4 w-8" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-32" />
                </div>
              ))}
            </div>
          ) : forms.length > 0 ? (
            <table className="w-full bg-white table-auto">
              <thead className="bg-subprimary text-primary font-normal text-sm sm:text-base rounded-t-lg">
                <tr>
                  <th className="main-th p-2">#</th>
                  <th className="main-th p-2">
                    {t("processManagement.formName")}
                  </th>
                  <th className="main-th p-2">
                    {t("processManagement.status")}
                  </th>
                  <th className="main-th p-2">
                    {t("processManagement.createdAt")}
                  </th>
                  <th className="main-th p-2">
                    {t("processManagement.updatedAt")}
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-600 text-sm sm:text-base font-light">
                {forms.map((processForm: any) => (
                  <tr
                    key={processForm.id}
                    className="border-b border-gray-200 hover:bg-gray-100"
                  >
                    <td className="main-td p-2">{processForm.order}</td>
                    <td className="main-td p-2">{processForm.form?.name || '-'}</td>
                    <td className="main-td p-2 capitalize">
                      {processForm.form?.status || '-'}
                    </td>
                    <td className="main-td p-2">
                      {processForm.form?.createdAt
                        ? dayjs(processForm.form.createdAt).format(
                            "MMM D, YYYY, h:mm A"
                          )
                        : "-"}
                    </td>
                    <td className="main-td p-2">
                      {processForm.form?.updatedAt
                        ? dayjs(processForm.form.updatedAt).format(
                            "MMM D, YYYY, h:mm A"
                          )
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-center text-gray-600 py-8">
              {t("processManagement.noFormsAvailableForThisProcess")}
            </p>
          )}
        </div>
        <div className="p-4 flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            {t("processManagement.close")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewProcessFormsModal;
